import React, { useState } from 'react';
import { useAuth } from '../../lib/AuthContext';
import { Button } from '../ui/Button';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../ui/Card';
import { Shield, User, Eye, EyeOff, ArrowRight } from 'lucide-react';

interface AnonymityChoiceProps {
  revieweeName: string;
  templateDescription?: string;
  onContinue: (isAnonymous: boolean) => void;
  onCancel?: () => void;
}

const AnonymityChoice: React.FC<AnonymityChoiceProps> = ({
  revieweeName,
  templateDescription,
  onContinue,
  onCancel
}) => {
  const { currentUser } = useAuth();
  const [selectedOption, setSelectedOption] = useState<boolean | null>(null);

  const handleContinue = () => {
    if (selectedOption !== null) {
      onContinue(selectedOption);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="w-full max-w-2xl">
        
        <CardHeader className="text-center">
          <div className="flex justify-center my-4">
            <CardTitle className="text-xl">
                You're about to provide feedback for <strong>{revieweeName}</strong>
            </CardTitle>
          </div>
            {templateDescription && (
              <div className="mb-6 px-4">
                <div className="p-4">
                  <div 
                    className="text-primary text-sm font-medium leading-relaxed prose prose-sm max-w-none"
                    dangerouslySetInnerHTML={{ 
                      __html: templateDescription
                        .replace(/\n/g, '<br/>')
                        .replace(/\* /g, '• ')
                        .replace(/- /g, '• ')
                    }}
                  />
                </div>
              </div>
            )}
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="text-center mb-6">
            <p className="text-muted-foreground">
              Before we begin, please choose how you'd like your feedback to be attributed when shared with {revieweeName}.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Include Name Option */}
            <div
              className={`p-6 border-2 rounded-lg cursor-pointer transition-all ${
                selectedOption === false
                  ? 'border-primary bg-primary/5'
                  : 'border-border hover:border-primary/50'
              }`}
              onClick={() => setSelectedOption(false)}
            >
              <div className="text-center space-y-4">
                <div className="flex justify-center">
                  <User className="h-8 w-8 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg">Include My Name</h3>
                  <p className="text-sm text-muted-foreground mt-2">
                    Your feedback will be attributed to you when shared with {revieweeName}. 
                    This promotes open communication and accountability.
                  </p>
                </div>
                <div className="bg-muted/50 p-3 rounded-md">
                  <p className="text-xs text-muted-foreground">
                    <strong>From:</strong> {currentUser?.displayName || currentUser?.email}
                  </p>
                </div>
                {selectedOption === false && (
                  <div className="flex items-center justify-center gap-2 text-primary">
                    <Eye className="h-4 w-4" />
                    <span className="text-sm font-medium">Selected</span>
                  </div>
                )}
              </div>
            </div>

            {/* Anonymous Option */}
            <div
              className={`p-6 border-2 rounded-lg cursor-pointer transition-all ${
                selectedOption === true
                  ? 'border-primary bg-primary/5'
                  : 'border-border hover:border-primary/50'
              }`}
              onClick={() => setSelectedOption(true)}
            >
              <div className="text-center space-y-4">
                <div className="flex justify-center">
                  <EyeOff className="h-8 w-8 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg">Remain Anonymous</h3>
                  <p className="text-sm text-muted-foreground mt-2">
                    Your feedback will be shared without your name. This allows for more 
                    candid feedback while maintaining privacy.
                  </p>
                </div>
                <div className="bg-muted/50 p-3 rounded-md">
                  <p className="text-xs text-muted-foreground">
                    <strong>From:</strong> Anonymous Colleague
                  </p>
                </div>
                {selectedOption === true && (
                  <div className="flex items-center justify-center gap-2 text-primary">
                    <EyeOff className="h-4 w-4" />
                    <span className="text-sm font-medium">Selected</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4">
            <Button
              onClick={handleContinue}
              disabled={selectedOption === null}
              className="flex-1 flex items-center justify-center gap-2"
            >
              <ArrowRight className="h-4 w-4" />
              Continue to Review
            </Button>
            {onCancel && (
              <Button variant="outline" onClick={onCancel}>
                Cancel
              </Button>
            )}
          </div>

          {selectedOption === null && (
            <p className="text-center text-sm text-muted-foreground">
              Please select an option to continue
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AnonymityChoice;
